import api from './axios';
import {
    FileListResponse,
    UpdateFilePayload,
    UpdateFileResponse,
    DocInvoice,
    UpdateInvoiceRequest,
    InvoiceListResponse,
    UploadDocumentResponse,
    InvoiceUploadStatusResponse,
    PriceIncreasesListResponse,
    PriceDecreasesListResponse,
    TagLabelsResponse,
    RepairPriceFlagsResponse,
    DuplicateInvoiceCheckResponse, QboWritePreflightResponse,
    WorkspaceTreeResponse,
    WorkspaceSummaryResponse,
    WorkspaceReclassifyResponse,
} from './types';

export interface UserGlobalFilesQuery {
    page?: number;
    pageSize?: number;
    businessId?: string;
    startDate?: string;
    endDate?: string;
    folderPath?: string;
    /** When true with folderPath, list files in that folder only (exact workspace path). */
    exactFolder?: boolean;
    q?: string;
    workspaceCategory?: string;
    /** e.g. 'email' for Mailgun-ingested files */
    sourceChannel?: string;
    emailId?: string;
}

const documentsApi = {

    // Upload Global File (original)
    async uploadGlobalFile(file: File, businessId?: string): Promise<{ status: string; message: string }> {
        try {
            const formData = new FormData();
            formData.append('file', file);
            if (businessId) {
                formData.append('business_id', businessId);
            }

            const res = await api.post('/chat/upload_global', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            return res.data;
        } catch (error: any) {
            console.error('Upload failed:', error);
            const detail = error?.response?.data?.detail;
            const message =
                typeof detail === 'object' && detail?.message
                    ? detail.message
                    : error?.response?.data?.message || 'Failed to upload file';
            throw new Error(message);
        }
    },

    // Upload Document with Type and Tags
    async uploadDocument(file: File, fileType: string, tags: string[], businessId?: string): Promise<UploadDocumentResponse> {
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('type', fileType);
            formData.append('tag', JSON.stringify(tags));
            if (businessId) {
                formData.append('business_id', businessId);
            }

            // 120s timeout for invoice/expenses/bills (LLM processing); 60s for other document types
            const timeout =
                fileType === 'invoice' || fileType === 'expenses' || fileType === 'bills' ? 120000 : 60000;

            const res = await api.post<UploadDocumentResponse>('/chat/upload_global', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout,
            });

            return res.data;
        } catch (error: any) {
            console.error('Upload failed:', error);
            const detail = error?.response?.data?.detail;
            const detailMessage =
                typeof detail === 'object' && detail?.message
                    ? detail.message
                    : detail;
            const rawMessage = String(error?.response?.data?.message || detailMessage || error?.message || '').toLowerCase();
            if (error.response?.status === 409 || rawMessage.includes('file already exists') || rawMessage.includes('already exists')) {
                throw new Error('File already exists, try another one.');
            }
            const msg = error.code === 'ECONNABORTED'
                ? 'Upload timed out. The server may be slow or overloaded. Try a smaller file or try again later.'
                : error?.response?.data?.message || detailMessage || 'Failed to upload file';
            throw new Error(msg);
        }
    },

    // Get a file download URL by filename
    async getDownloadUrl(filename: string, inline: boolean = false, businessId?: string): Promise<{ url: string }> {
        try {
            const res = await api.get('/chat/download_file', {
                params: { filename, inline, ...(businessId && { business_id: businessId }) },
            });

            return res.data;
        } catch (error: any) {
            console.error('Failed to get download URL:', error);
            throw new Error(error.response?.data?.message || 'Failed to retrieve download URL');
        }
    },

    async getDownloadBlob(filename: string, businessId?: string): Promise<Blob> {
        try {
            const res = await api.get('/chat/download_file_content', {
                params: { filename, ...(businessId && { business_id: businessId }) },
                responseType: 'blob',
            });
            return res.data;
        } catch (error: any) {
            console.error('Failed to get download blob:', error);
            throw new Error(error.response?.data?.message || 'Failed to retrieve document content');
        }
    },

    // Get List of User Global Files (workspace filters: folderPath prefix, q, workspaceCategory)
    async getUserGlobalFiles(query: UserGlobalFilesQuery = {}): Promise<FileListResponse> {
        try {
            const {
                page = 1,
                pageSize = 10,
                businessId,
                startDate,
                endDate,
                folderPath,
                exactFolder,
                q,
                workspaceCategory,
                sourceChannel,
                emailId,
            } = query;
            const res = await api.get('/chat/user_global_files', {
                params: {
                    page,
                    page_size: pageSize,
                    ...(businessId && { business_id: businessId }),
                    ...(startDate && { start_date: startDate }),
                    ...(endDate && { end_date: endDate }),
                    ...(folderPath && { folder_path: folderPath }),
                    ...(exactFolder && { exact_folder: true }),
                    ...(q && { q }),
                    ...(workspaceCategory && { workspace_category: workspaceCategory }),
                    ...(sourceChannel && { source_channel: sourceChannel }),
                    ...(emailId && { email_id: emailId }),
                },
            });
            return res.data;
        } catch (error: any) {
            console.error('Failed to fetch user files:', error);
            throw new Error(error.response?.data?.message || 'Failed to fetch uploaded files');
        }
    },

    async getUserGlobalFile(
        fileId: string,
        businessId?: string,
    ): Promise<{
        file_id: string;
        filename: string;
        original_filename: string;
        type?: string;
        upload_time?: string;
        workspace_category?: string;
        workspace_entity?: string;
        workspace_folder_path?: string;
        /** Present for Google Sheets imports (Finz Docs row). */
        source_channel?: string;
    }> {
        try {
            const res = await api.get(`/chat/user_global_files/${fileId}`, {
                params: businessId ? { business_id: businessId } : undefined,
            });
            return res.data;
        } catch (error: any) {
            console.error('Failed to fetch uploaded file:', error);
            throw new Error(error.response?.data?.message || 'Failed to fetch uploaded file');
        }
    },

    async getWorkspaceTree(businessId?: string): Promise<WorkspaceTreeResponse> {
        try {
            const res = await api.get<WorkspaceTreeResponse>('/chat/workspace_tree', {
                params: businessId ? { business_id: businessId } : undefined,
            });
            return res.data;
        } catch (error: any) {
            console.error('Failed to fetch workspace tree:', error);
            throw new Error(error.response?.data?.detail || 'Failed to load workspace folders');
        }
    },

    async getWorkspaceSummary(businessId?: string): Promise<WorkspaceSummaryResponse> {
        try {
            const res = await api.get<WorkspaceSummaryResponse>('/chat/workspace_summary', {
                params: businessId ? { business_id: businessId } : undefined,
            });
            return res.data;
        } catch (error: any) {
            console.error('Failed to fetch workspace summary:', error);
            throw new Error(error.response?.data?.detail || 'Failed to load workspace summary');
        }
    },

    /** Move files or an entire folder in the virtual workspace. S3 sync runs in the background. */
    async moveWorkspaceFiles(payload: {
        targetFolderPath: string;
        fileIds?: string[];
        sourceFolderPath?: string;
        businessId?: string;
    }): Promise<{
        status: string;
        updated: number;
        target_folder_path?: string;
        file_ids?: string[];
        s3_sync?: string;
        message?: string;
    }> {
        try {
            const res = await api.post(`/chat/user_global_files/move_workspace`, {
                target_folder_path: payload.targetFolderPath,
                file_ids: payload.fileIds?.length ? payload.fileIds : undefined,
                source_folder_path: payload.sourceFolderPath?.trim() || undefined,
            }, {
                params: payload.businessId ? { business_id: payload.businessId } : undefined,
            });
            return res.data;
        } catch (error: any) {
            console.error('Workspace move failed:', error);
            const status = error?.response?.status;
            const detail =
                error?.response?.data?.detail ||
                error?.response?.data?.message ||
                (status === 405
                    ? 'Workspace move is not available on this API version. Rebuild or redeploy the backend.'
                    : 'Failed to move document(s).');
            throw new Error(typeof detail === 'string' ? detail : 'Failed to move document(s).');
        }
    },

    /** Rename a virtual entity folder (Mongo immediately; S3 relocate in background when files move). */
    async renameWorkspaceFolder(payload: {
        folderPath: string;
        newName: string;
        businessId?: string;
    }): Promise<{ status: string; folder_path: string; s3_sync?: string }> {
        try {
            const res = await api.post(
                '/chat/user_global_files/rename_workspace_folder',
                {
                    folder_path: payload.folderPath,
                    new_name: payload.newName,
                },
                {
                    params: payload.businessId ? { business_id: payload.businessId } : undefined,
                },
            );
            return res.data;
        } catch (error: any) {
            console.error('Rename workspace folder failed:', error);
            const detail =
                error?.response?.data?.detail ||
                error?.response?.data?.message ||
                'Could not rename folder.';
            throw new Error(typeof detail === 'string' ? detail : 'Could not rename folder.');
        }
    },

    /** Create an empty virtual folder under a category (no S3 until files are moved in). */
    async createWorkspaceFolder(payload: {
        parentFolderPath: string;
        name: string;
        businessId?: string;
    }): Promise<{ status: string; folder_path: string }> {
        try {
            const res = await api.post(
                '/chat/user_global_files/create_workspace_folder',
                {
                    parent_folder_path: payload.parentFolderPath,
                    name: payload.name,
                },
                {
                    params: payload.businessId ? { business_id: payload.businessId } : undefined,
                },
            );
            return res.data;
        } catch (error: any) {
            console.error('Create workspace folder failed:', error);
            const detail =
                error?.response?.data?.detail ||
                error?.response?.data?.message ||
                'Could not create folder.';
            throw new Error(typeof detail === 'string' ? detail : 'Could not create folder.');
        }
    },

    async deleteWorkspaceFolder(payload: {
        folderPath: string;
        businessId?: string;
    }): Promise<{ status: string; deleted_folder_path: string; deleted_marker_paths: string[] }> {
        try {
            const res = await api.post(
                '/chat/user_global_files/delete_workspace_folder',
                {
                    folder_path: payload.folderPath,
                },
                {
                    params: payload.businessId ? { business_id: payload.businessId } : undefined,
                },
            );
            return res.data;
        } catch (error: any) {
            console.error('Delete workspace folder failed:', error);
            const detail =
                error?.response?.data?.detail ||
                error?.response?.data?.message ||
                'Could not delete folder.';
            throw new Error(typeof detail === 'string' ? detail : 'Could not delete folder.');
        }
    },

    /** Re-run virtual-folder classification from S3 (admin / support). Does not re-index vectors. */
    async reclassifyWorkspaceFile(
        fileId: string,
        options?: { businessId?: string; forceLlm?: boolean }
    ): Promise<WorkspaceReclassifyResponse> {
        try {
            const params: Record<string, string | boolean> = {};
            if (options?.businessId) params.business_id = options.businessId;
            if (options?.forceLlm) params.force_llm = true;
            const res = await api.post<WorkspaceReclassifyResponse>(
                `/chat/user_global_files/${fileId}/reclassify`,
                null,
                { params }
            );
            return res.data;
        } catch (error: any) {
            console.error('Workspace reclassify failed:', error);
            throw new Error(
                error?.response?.data?.detail || error?.response?.data?.message || 'Failed to reclassify document'
            );
        }
    },

    /** Retry processing for a document stuck in 'Processing' (invoice/bill only) */
    retryFileProcessing: async (fileId: string, businessId?: string): Promise<{ status: string; job_id: string; message: string }> => {
        try {
            const res = await api.post<{ status: string; job_id: string; message: string }>(
                `/chat/files/${fileId}/retry_processing`,
                null,
                { params: businessId ? { business_id: businessId } : undefined }
            );
            return res.data;
        } catch (error: any) {
            console.error('Retry processing failed:', error);
            throw new Error(error?.response?.data?.detail || 'Failed to retry processing');
        }
    },

    // Edit or Delete Global File
    updateFile: async (fileId: string, data: UpdateFilePayload, businessId?: string): Promise<UpdateFileResponse> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.patch<UpdateFileResponse>(`/chat/user_global_files/${fileId}`, data, { params });
            return response.data;
        } catch (error: any) {
            console.error("Failed to update file:", error);
            throw new Error(
                error?.response?.data?.detail || error?.response?.data?.message || "An error occurred while updating the file."
            );
        }
    },

    // Invoice API Methods
    getInvoiceByFileId: async (fileId: string, businessId?: string): Promise<DocInvoice> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.get<DocInvoice>(`/api/invoices/by-file/${fileId}`, { params });
            return response.data;
        } catch (error: any) {
            if (error?.response?.status === 404) {
                throw new Error('Invoice not ready yet. Processing may still be in progress.');
            }
            console.error("Failed to fetch invoice by file:", error);
            throw new Error(
                error?.response?.data?.detail || "An error occurred while fetching the invoice."
            );
        }
    },

    getExpenseByFileId: async (fileId: string, businessId?: string): Promise<DocInvoice> => {
        return documentsApi.getInvoiceByFileId(fileId, businessId);
    },

    getInvoice: async (invoiceId: string, businessId?: string): Promise<DocInvoice> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.get<DocInvoice>(`/api/invoices/${invoiceId}`, { params });
            return response.data;
        } catch (error: any) {
            console.error("Failed to fetch invoice:", error);
            throw new Error(
                error?.response?.data?.detail || "An error occurred while fetching the invoice."
            );
        }
    },

    getExpense: async (expenseId: string, businessId?: string): Promise<DocInvoice> => {
        return documentsApi.getInvoice(expenseId, businessId);
    },

    updateInvoice: async (invoiceId: string, data: UpdateInvoiceRequest, businessId?: string): Promise<DocInvoice> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.put<DocInvoice>(`/api/invoices/${invoiceId}`, data, { params });
            return response.data;
        } catch (error: any) {
            console.error("Failed to update invoice:", error);
            throw new Error(
                error?.response?.data?.detail || "An error occurred while updating the invoice."
            );
        }
    },

    updateExpense: async (expenseId: string, data: UpdateInvoiceRequest, businessId?: string): Promise<DocInvoice> => {
        return documentsApi.updateInvoice(expenseId, data, businessId);
    },

    preflightInvoiceQboSync: async (invoiceId: string, data: UpdateInvoiceRequest, businessId?: string): Promise<QboWritePreflightResponse> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.post<QboWritePreflightResponse>(`/api/invoices/${invoiceId}/qbo-preflight`, data, { params });
            return response.data;
        } catch (error: any) {
            console.error("Failed to preflight invoice QBO sync:", error);
            throw new Error(
                error?.response?.data?.detail || "An error occurred while checking QuickBooks for this invoice."
            );
        }
    },

    confirmAndSyncInvoiceToQbo: async (invoiceId: string, data: UpdateInvoiceRequest, businessId?: string): Promise<DocInvoice> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.post<DocInvoice>(`/api/invoices/${invoiceId}/confirm-and-sync-qbo`, data, { params });
            return response.data;
        } catch (error: any) {
            console.error("Failed to confirm and sync invoice to QBO:", error);
            throw new Error(
                error?.response?.data?.detail || "An error occurred while writing the invoice to QuickBooks."
            );
        }
    },

    queueInvoiceQboSync: async (invoiceId: string, businessId?: string): Promise<{ status: string; document_id: string; document_type: string }> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.post<{ status: string; document_id: string; document_type: string }>(`/api/invoices/${invoiceId}/queue-qbo-sync`, null, { params });
            return response.data;
        } catch (error: any) {
            console.error("Failed to queue invoice QBO sync:", error);
            throw new Error(
                error?.response?.data?.detail || "An error occurred while queueing the invoice for QuickBooks sync."
            );
        }
    },

    checkDuplicateInvoice: async (invoiceId: string, data: UpdateInvoiceRequest, businessId?: string): Promise<DuplicateInvoiceCheckResponse> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.post<DuplicateInvoiceCheckResponse>(`/api/invoices/${invoiceId}/duplicate-check`, data, { params });
            return response.data;
        } catch (error: any) {
            console.error("Failed to check duplicate invoice:", error);
            throw new Error(
                error?.response?.data?.detail || "An error occurred while checking the invoice number."
            );
        }
    },

    checkDuplicateExpense: async (expenseId: string, data: UpdateInvoiceRequest, businessId?: string): Promise<DuplicateInvoiceCheckResponse> => {
        return documentsApi.checkDuplicateInvoice(expenseId, data, businessId);
    },

    combineInvoicePages: async (invoiceId: string, targetInvoiceId: string, data: UpdateInvoiceRequest, businessId?: string, sourceData?: UpdateInvoiceRequest): Promise<DocInvoice> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.post<DocInvoice>(`/api/invoices/${invoiceId}/combine-pages`, {
                target_invoice_id: targetInvoiceId,
                data: data.data,
                source_data: sourceData?.data,
            }, { params });
            return response.data;
        } catch (error: any) {
            console.error("Failed to combine invoice pages:", error);
            throw new Error(
                error?.response?.data?.detail || "An error occurred while combining invoice pages."
            );
        }
    },

    combineExpensePages: async (expenseId: string, targetExpenseId: string, data: UpdateInvoiceRequest, businessId?: string, sourceData?: UpdateInvoiceRequest): Promise<DocInvoice> => {
        return documentsApi.combineInvoicePages(expenseId, targetExpenseId, data, businessId, sourceData);
    },

    linkDuplicateInvoice: async (invoiceId: string, targetInvoiceId: string, data: UpdateInvoiceRequest, businessId?: string, sourceData?: UpdateInvoiceRequest): Promise<DocInvoice> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.post<DocInvoice>(`/api/invoices/${invoiceId}/link-duplicate`, {
                target_invoice_id: targetInvoiceId,
                data: data.data,
                source_data: sourceData?.data,
            }, { params });
            return response.data;
        } catch (error: any) {
            console.error("Failed to link duplicate invoice:", error);
            throw new Error(
                error?.response?.data?.detail || "An error occurred while linking duplicate invoices."
            );
        }
    },

    linkDuplicateExpense: async (expenseId: string, targetExpenseId: string, data: UpdateInvoiceRequest, businessId?: string, sourceData?: UpdateInvoiceRequest): Promise<DocInvoice> => {
        return documentsApi.linkDuplicateInvoice(expenseId, targetExpenseId, data, businessId, sourceData);
    },

    deleteInvoice: async (invoiceId: string, businessId?: string): Promise<{ status: string; message: string; invoice_id: string }> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.delete<{ status: string; message: string; invoice_id: string }>(`/api/invoices/${invoiceId}`, { params });
            return response.data;
        } catch (error: any) {
            console.error("Failed to delete invoice:", error);
            throw new Error(
                error?.response?.data?.detail || "An error occurred while deleting the invoice."
            );
        }
    },

    deleteExpense: async (expenseId: string, businessId?: string): Promise<{ status: string; message: string; invoice_id: string }> => {
        return documentsApi.deleteInvoice(expenseId, businessId);
    },

    deleteInvoicesByFileId: async (fileId: string, businessId?: string): Promise<{ status: string; message: string; file_id: string; deleted_count: number }> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.delete<{ status: string; message: string; file_id: string; deleted_count: number }>(`/api/invoices/by-file/${fileId}`, { params });
            return response.data;
        } catch (error: any) {
            console.error("Failed to delete invoices by file:", error);
            throw new Error(
                error?.response?.data?.detail || "An error occurred while deleting documents for this file."
            );
        }
    },

    listInvoices: async (status?: string, page: number = 1, pageSize: number = 20, businessId?: string): Promise<InvoiceListResponse> => {
        try {
            const params: any = { page, page_size: pageSize };
            if (status) params.status = status;
            if (businessId) params.business_id = businessId;
            const response = await api.get<InvoiceListResponse>('/api/invoices', { params });
            return response.data;
        } catch (error: any) {
            console.error("Failed to list invoices:", error);
            throw new Error(
                error?.response?.data?.detail || "An error occurred while listing invoices."
            );
        }
    },

    // Bill API Methods (same shape as invoice, separate collection)
    getBillByFileId: async (fileId: string, businessId?: string): Promise<DocInvoice> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.get<DocInvoice>(`/api/bills/by-file/${fileId}`, { params });
            return response.data;
        } catch (error: any) {
            if (error?.response?.status === 404) {
                throw new Error('Bill not ready yet. Processing may still be in progress.');
            }
            console.error("Failed to fetch bill by file:", error);
            throw new Error(
                error?.response?.data?.detail || "An error occurred while fetching the bill."
            );
        }
    },

    getBill: async (billId: string, businessId?: string): Promise<DocInvoice> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.get<DocInvoice>(`/api/bills/${billId}`, { params });
            return response.data;
        } catch (error: any) {
            console.error("Failed to fetch bill:", error);
            throw new Error(
                error?.response?.data?.detail || "An error occurred while fetching the bill."
            );
        }
    },

    updateBill: async (billId: string, data: UpdateInvoiceRequest, businessId?: string): Promise<DocInvoice> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.put<DocInvoice>(`/api/bills/${billId}`, data, { params });
            return response.data;
        } catch (error: any) {
            console.error("Failed to update bill:", error);
            throw new Error(
                error?.response?.data?.detail || "An error occurred while updating the bill."
            );
        }
    },

    checkDuplicateBill: async (billId: string, data: UpdateInvoiceRequest, businessId?: string): Promise<DuplicateInvoiceCheckResponse> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.post<DuplicateInvoiceCheckResponse>(`/api/bills/${billId}/duplicate-check`, data, { params });
            return response.data;
        } catch (error: any) {
            console.error("Failed to check duplicate bill:", error);
            throw new Error(
                error?.response?.data?.detail || "An error occurred while checking the bill number."
            );
        }
    },

    preflightBillQboSync: async (billId: string, data: UpdateInvoiceRequest, businessId?: string): Promise<QboWritePreflightResponse> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.post<QboWritePreflightResponse>(`/api/bills/${billId}/qbo-preflight`, data, { params });
            return response.data;
        } catch (error: any) {
            console.error("Failed to preflight bill QBO sync:", error);
            throw new Error(
                error?.response?.data?.detail || "An error occurred while checking QuickBooks."
            );
        }
    },

    confirmAndSyncBillToQbo: async (billId: string, data: UpdateInvoiceRequest, businessId?: string): Promise<DocInvoice> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.post<DocInvoice>(`/api/bills/${billId}/confirm-and-sync-qbo`, data, { params });
            return response.data;
        } catch (error: any) {
            console.error("Failed to confirm and sync bill to QBO:", error);
            throw new Error(
                error?.response?.data?.detail || "An error occurred while writing the bill to QuickBooks."
            );
        }
    },

    queueBillQboSync: async (billId: string, businessId?: string): Promise<{ status: string; document_id: string; document_type: string }> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.post<{ status: string; document_id: string; document_type: string }>(`/api/bills/${billId}/queue-qbo-sync`, null, { params });
            return response.data;
        } catch (error: any) {
            console.error("Failed to queue bill QBO sync:", error);
            throw new Error(
                error?.response?.data?.detail || "An error occurred while queueing the bill for QuickBooks sync."
            );
        }
    },

    combineBillPages: async (billId: string, targetBillId: string, data: UpdateInvoiceRequest, businessId?: string, sourceData?: UpdateInvoiceRequest): Promise<DocInvoice> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.post<DocInvoice>(`/api/bills/${billId}/combine-pages`, {
                target_invoice_id: targetBillId,
                data: data.data,
                source_data: sourceData?.data,
            }, { params });
            return response.data;
        } catch (error: any) {
            console.error("Failed to combine bill pages:", error);
            throw new Error(
                error?.response?.data?.detail || "An error occurred while combining bill pages."
            );
        }
    },

    linkDuplicateBill: async (billId: string, targetBillId: string, data: UpdateInvoiceRequest, businessId?: string, sourceData?: UpdateInvoiceRequest): Promise<DocInvoice> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.post<DocInvoice>(`/api/bills/${billId}/link-duplicate`, {
                target_invoice_id: targetBillId,
                data: data.data,
                source_data: sourceData?.data,
            }, { params });
            return response.data;
        } catch (error: any) {
            console.error("Failed to link duplicate bill:", error);
            throw new Error(
                error?.response?.data?.detail || "An error occurred while linking duplicate bills."
            );
        }
    },

    deleteBill: async (billId: string, businessId?: string): Promise<{ status: string; message: string; invoice_id: string }> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.delete<{ status: string; message: string; invoice_id: string }>(`/api/bills/${billId}`, { params });
            return response.data;
        } catch (error: any) {
            console.error("Failed to delete bill:", error);
            throw new Error(
                error?.response?.data?.detail || "An error occurred while deleting the bill."
            );
        }
    },

    deleteBillsByFileId: async (fileId: string, businessId?: string): Promise<{ status: string; message: string; file_id: string; deleted_count: number }> => {
        try {
            const params = businessId ? { business_id: businessId } : {};
            const response = await api.delete<{ status: string; message: string; file_id: string; deleted_count: number }>(`/api/bills/by-file/${fileId}`, { params });
            return response.data;
        } catch (error: any) {
            console.error("Failed to delete bills by file:", error);
            throw new Error(
                error?.response?.data?.detail || "An error occurred while deleting documents for this file."
            );
        }
    },

    listBills: async (status?: string, page: number = 1, pageSize: number = 20, businessId?: string): Promise<InvoiceListResponse> => {
        try {
            const params: any = { page, page_size: pageSize };
            if (status) params.status = status;
            if (businessId) params.business_id = businessId;
            const response = await api.get<InvoiceListResponse>('/api/bills', { params });
            return response.data;
        } catch (error: any) {
            console.error("Failed to list bills:", error);
            throw new Error(
                error?.response?.data?.detail || "An error occurred while listing bills."
            );
        }
    },

    getInvoiceUploadStatus: async (jobId: string, businessId?: string): Promise<InvoiceUploadStatusResponse> => {
        try {
            const response = await api.get<InvoiceUploadStatusResponse>(
                `/chat/invoice_upload_status/${jobId}`,
                { params: businessId ? { business_id: businessId } : undefined }
            );
            return response.data;
        } catch (error: any) {
            console.error("Failed to get invoice upload status:", error);
            throw new Error(
                error?.response?.data?.detail || "An error occurred while checking upload status."
            );
        }
    },

    getRelatedTransactionsByFile: async (fileId: string): Promise<{ items: Array<DocInvoice & { _doc_type?: 'invoice' | 'bill'; _s3_filename?: string }> }> => {
        try {
            const response = await api.get<{ items: Array<DocInvoice & { _doc_type?: 'invoice' | 'bill'; _s3_filename?: string }> }>(
                `/api/invoices/related-by-file/${fileId}`
            );
            return response.data;
        } catch (error: any) {
            if (error?.response?.status === 404) {
                throw new Error('Document not found. Processing may still be in progress.');
            }
            console.error('Failed to fetch related transactions:', error);
            throw new Error(
                error?.response?.data?.detail || 'Failed to fetch related transactions.'
            );
        }
    },

    getPriceIncreases: async (fileId: string, limit: number = 50, businessId?: string): Promise<PriceIncreasesListResponse> => {
        try {
            const response = await api.get<PriceIncreasesListResponse>('/notifications/price-increases', {
                params: {
                    file_id: fileId,
                    limit,
                    ...(businessId ? { business_id: businessId } : {}),
                },
            });
            return response.data;
        } catch (error: any) {
            console.error("Failed to fetch price increases:", error);
            throw new Error(
                error?.response?.data?.detail || "An error occurred while fetching price increases."
            );
        }
    },

    getPriceDecreases: async (fileId: string, limit: number = 50, businessId?: string): Promise<PriceDecreasesListResponse> => {
        try {
            const response = await api.get<PriceDecreasesListResponse>('/notifications/price-decreases', {
                params: {
                    file_id: fileId,
                    limit,
                    ...(businessId ? { business_id: businessId } : {}),
                },
            });
            return response.data;
        } catch (error: any) {
            console.error("Failed to fetch price decreases:", error);
            throw new Error(
                error?.response?.data?.detail || "An error occurred while fetching price decreases."
            );
        }
    },

    getTagLabels: async (businessId?: string): Promise<TagLabelsResponse> => {
        try {
            const response = await api.get<TagLabelsResponse>('/chat/tag_labels', {
                params: businessId ? { business_id: businessId } : undefined,
            });
            return response.data;
        } catch (error: any) {
            console.error("Failed to fetch tag labels:", error);
            throw new Error(
                error?.response?.data?.detail || "An error occurred while fetching tag labels."
            );
        }
    },

    addTagLabel: async (label: string, businessId?: string): Promise<{ status: string; label: string }> => {
        try {
            const response = await api.post<{ status: string; label: string }>(
                '/chat/tag_labels',
                { label },
                { params: businessId ? { business_id: businessId } : undefined }
            );
            return response.data;
        } catch (error: any) {
            console.error("Failed to add tag label:", error);
            throw new Error(
                error?.response?.data?.detail || "An error occurred while adding tag label."
            );
        }
    },

    deleteTagLabel: async (label: string, businessId?: string): Promise<{ status: string; removed_label_records: number; updated_files: number }> => {
        try {
            const response = await api.delete<{ status: string; removed_label_records: number; updated_files: number }>(
                `/chat/tag_labels/${encodeURIComponent(label)}`,
                { params: businessId ? { business_id: businessId } : undefined }
            );
            return response.data;
        } catch (error: any) {
            console.error("Failed to delete tag label:", error);
            throw new Error(
                error?.response?.data?.detail || "An error occurred while deleting tag label."
            );
        }
    },

    repairPriceFlags: async (
        options?: {
            fileId?: string;
            businessId?: string;
            limit?: number;
            dryRun?: boolean;
            onlyFlagged?: boolean;
        }
    ): Promise<RepairPriceFlagsResponse> => {
        const { fileId, businessId, limit, dryRun, onlyFlagged } = options || {};
        try {
            const response = await api.post<RepairPriceFlagsResponse>(
                '/notifications/price-flags/repair',
                null,
                {
                    params: {
                        ...(fileId ? { file_id: fileId } : {}),
                        ...(businessId ? { business_id: businessId } : {}),
                        ...(typeof limit === 'number' ? { limit } : {}),
                        ...(typeof dryRun === 'boolean' ? { dry_run: dryRun } : {}),
                        ...(typeof onlyFlagged === 'boolean' ? { only_flagged: onlyFlagged } : {}),
                    },
                }
            );
            return response.data;
        } catch (error: any) {
            console.error("Failed to repair price flags:", error);
            throw new Error(
                error?.response?.data?.detail || "An error occurred while repairing price flags."
            );
        }
    },
};

export default documentsApi;
